import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";

import _fromPairs from "lodash/fromPairs";
import classNames from "classnames";
import { TranslationFn } from "lib/types";

import { OutlinedButton } from "common/DS/Buttons";
import FollowUp from "./FollowUp";
import AnswerDisplay from "./AnswerDisplay";
import QuestionButtonGroup from "./QuestionButtonGroup";
import { HandleAnswerFnProps, QuestionComponentType, QuestionProps } from "./types";

const SelectMany: QuestionComponentType = (
    {
        handleAnswer,
        isLastQuestion,
        toggleAnonymous,
        isAnonymous,
        handleSkip,
        handleFollowUpStart,
        handleFollowUpBack,
        isRequired,
        ...selectProps
    }: QuestionProps,
    { t: _ }: { t: TranslationFn },
) => {
    const { answer, id, question } = selectProps;
    const [followUpOptional, setFollowUpOptional] = useState(false);
    const [followUp, setFollowUp] = useState("");
    const [showFollowUp, setShowFollowUp] = useState(false);
    const options = answer;
    const [showAnswer, setShowAnswer] = useState(true);
    const [selected, setSelected] = useState(
        _fromPairs(options.map(({ value }) => [value, false])),
    );
    const [isDirty, setIsDirty] = useState(false);

    // Toggle the given selection value within the selected state
    const handleSelect = (value: string) => {
        if (!isDirty) setIsDirty(true);
        setSelected((prev) => ({ ...prev, [value]: !prev[value] }));
    };

    const createOnSelect = useCallback<
        (value: string) => React.MouseEventHandler<HTMLButtonElement>
    >(
        (value) => (e) => {
            if (e.target instanceof HTMLButtonElement) {
                e.target.blur();
            }
            handleSelect(value);
        },
        [],
    );

    const handleFollowUp = () => {
        const selectedValues = Object.keys(selected).filter((k) => selected[k] === true);
        const selectedAnswerIds = answer
            .filter((a) => selectedValues.includes(a.value))
            .map((a) => a.id);
        const data: HandleAnswerFnProps = {
            questionID: id,
            response: selectedAnswerIds,
            responseText: null,
        };
        if (followUp) {
            data.followUp = followUp;
        }
        handleAnswer({
            ...data,
        });
    };

    const handleLocalFollowUpBack = () => {
        if (followUp) {
            setFollowUp("");
        }
        setSelected(_fromPairs(options.map(({ value }) => [value, false])));
        setShowAnswer(true);
        setShowFollowUp(false);
        handleFollowUpBack();
    };

    const onSubmit = () => {
        const selectedValues = Object.keys(selected).filter((k) => selected[k] === true);
        const selectedAnswerIds = answer
            .filter((a) => selectedValues.includes(a.value))
            .map((a) => a.id);
        const selectedsFollowUps = answer
            .filter((a) => selectedValues.includes(a.value))
            .map((a) => !!a.has_follow_up);
        const selectedsFollowUpOptionals = answer
            .filter((a) => selectedValues.includes(a.value))
            .map((a) => (a.follow_up_optional !== undefined ? !!a.follow_up_optional : true));
        let followUpChoiceSelected = false;
        if (selectedsFollowUps.some((s) => s === true)) {
            followUpChoiceSelected = true;
        }
        let followUpRequiredChoiceSelected = false;
        if (selectedsFollowUpOptionals.some((s) => s === false)) {
            followUpRequiredChoiceSelected = true;
        }

        const showFollowUpLocal = showFollowUp || followUpChoiceSelected;
        const data: HandleAnswerFnProps = {
            questionID: id,
            response: selectedAnswerIds,
            responseText: null,
            canSubmit: selectedAnswerIds.length > 0 && !showFollowUpLocal,
        };
        if (followUp && followUp !== selectProps.follow_up && showFollowUpLocal) {
            data.followUp = followUp;
        }

        handleAnswer({
            ...data,
        });
        if (showFollowUpLocal) {
            setShowFollowUp(true);
            setFollowUpOptional(!followUpRequiredChoiceSelected);
            setShowAnswer(false);
            handleFollowUpStart({
                ...data,
            });
        }
    };

    const onFollowUp = (val: string) => {
        setFollowUp(val);
    };

    const midpoint = Math.round(options.length / 2);
    const columns =
        options.length <= 5 ? [options] : [options.slice(0, midpoint), options.slice(midpoint)];
    const totalSelected = Object.values(selected).filter(Boolean).length;
    const showError = isDirty && isRequired && totalSelected < 1;
    const errorMsg = showError ? _("Choose at least one") : null;

    const answerStyle: React.CSSProperties = {};
    if (!showAnswer) {
        answerStyle.display = "none";
    }
    return (
        <div>
            <div
                className="questions__follow-up questions__follow-up-multi-select label-group"
                style={answerStyle}
            >
                <label>{question}</label>
                <div
                    className={classNames("questions__follow-up-multi-select-group", {
                        "questions__follow-up-multi-select-group--single-col": columns.length === 1,
                    })}
                >
                    {columns.map((colOptions, idx) => (
                        <div
                            className="questions__follow-up-multi-select-group-column"
                            key={`options-col-${idx}`}
                        >
                            {colOptions.map(({ value }) => (
                                <OutlinedButton
                                    key={value}
                                    onClick={createOnSelect(value)}
                                    selected={selected[value]}
                                    size="large"
                                >
                                    <AnswerDisplay text={value} />
                                </OutlinedButton>
                            ))}
                        </div>
                    ))}
                </div>
                <div className="help-block">{errorMsg}</div>
                <QuestionButtonGroup
                    nextDisabled={totalSelected < 1}
                    handleSkip={handleSkip}
                    handleSubmit={onSubmit}
                    handleBack={handleFollowUpBack}
                    isLastQuestion={isLastQuestion}
                    forceShowSkip={!isRequired}
                    isRequired={isRequired}
                />
            </div>

            {showFollowUp && (
                <FollowUp
                    {...selectProps}
                    additionalClassName="questions_follow-up__after-multiselect"
                    handleChange={onFollowUp}
                    handleSubmit={handleFollowUp}
                    handleSkip={handleSkip}
                    handleBack={handleLocalFollowUpBack}
                    visible
                    optional={followUpOptional}
                    isLastQuestion={isLastQuestion}
                    toggleAnonymous={toggleAnonymous}
                    isAnonymous={isAnonymous}
                    isRequired={!followUpOptional}
                    questionText={selectProps.follow_up || ""}
                    selectedAnswerData={{
                        response: Object.entries(selected)
                            .filter(([, v]) => v)
                            .map(([k]) => k),
                    }}
                />
            )}
        </div>
    );
};
SelectMany.contextTypes = {
    t: PropTypes.func,
};

export default SelectMany;
